// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Accounting home page route.
 *
 * Provides a hub for all accounting operations including ledger configuration,
 * asset snapshots, rollforwards, exceptions, and the close workflow.
 */
import { useInitialTrueUps } from '@/accounting/hooks/use-initial-true-ups';
import { Card } from '@/components/card';
import { PageContent } from '@/components/page-content';
import {
  AccountBalanceOutlined,
  AddShoppingCartOutlined,
  BoltOutlined,
  CategoryOutlined,
  DeleteOutlined,
  EqualizerOutlined,
  EventBusyOutlined,
  HistoryOutlined,
  LockOutlined,
  PlaylistRemoveOutlined,
  SellOutlined,
  ShoppingCartCheckoutOutlined,
  StackedBarChartOutlined,
  TableChartOutlined,
  TuneOutlined,
  WaterfallChartOutlined,
} from '@/constants/hardfin-standard-icons';
import { ReportLink } from '@/report/components/report-link';
import { Stack, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_app/accounting/')({
  component: AccountingRoute,
});

/**
 * Renders the main accounting page, organizing accounting operations into categories.
 * Displays configuration, reporting, exceptions, and close workflow in a 2x2 grid layout.
 */
function AccountingRoute() {
  // TODO(#15943): Use initialTrueUps to conditionally show/hide close workflow items
  useInitialTrueUps();

  return (
    <PageContent>
      <Stack direction="row" spacing={4}>
        {/* Left column */}
        <Stack flex={1} spacing={3}>
          <Typography variant="h6" paddingLeft={1}>
            Setup and snapshots
          </Typography>
          {/* Configuration - top left */}
          <Card title="Configuration">
            <ReportLink
              to="/organization"
              hash="ledger-accounts"
              icon={<AccountBalanceOutlined />}
              title="Ledger accounts"
              subtitle={
                <span>
                  Configure <b>GL accounts</b> for journal entries
                </span>
              }
            />
            <ReportLink
              to="/catalog/items"
              search={{ type: 'device' }}
              icon={<CategoryOutlined />}
              title="Product defaults"
              subtitle={
                <span>
                  Set default <b>cost basis</b>, <b>useful life</b>, and{' '}
                  <b>salvage value</b> for products
                </span>
              }
            />
          </Card>
          {/* Asset snapshot reporting - top right */}
          <Card title="Asset snapshot reporting">
            <ReportLink
              to="/reports/asset-snapshot/$tabType"
              params={{ tabType: 'inventory' }}
              icon={<EqualizerOutlined />}
              title="Inventory snapshot"
              subtitle={
                <span>
                  Report on <b>inventory</b> assets as of a specific date
                </span>
              }
            />
            <ReportLink
              to="/reports/asset-snapshot/$tabType"
              params={{ tabType: 'fixed-assets' }}
              icon={<BoltOutlined />}
              title="Fixed asset snapshot"
              subtitle={
                <span>
                  Report on <b>fixed</b> assets as of a specific date
                </span>
              }
            />
            <ReportLink
              to="/reports/asset-snapshot/$tabType"
              params={{ tabType: 'sold-assets' }}
              icon={<SellOutlined />}
              title="Sold asset snapshot"
              subtitle={
                <span>
                  Report on <b>sold</b> assets as of a specific date
                </span>
              }
            />
            <ReportLink
              to="/reports/asset-snapshot/$tabType"
              params={{ tabType: 'scrapped-assets' }}
              icon={<DeleteOutlined />}
              title="Scrapped asset snapshot"
              subtitle={
                <span>
                  Report on <b>scrapped</b> assets as of a specific date
                </span>
              }
            />
          </Card>
        </Stack>
        <Stack flex={1} spacing={3}>
          <Typography variant="h6" paddingLeft={1}>
            Rollforwards and bridges
          </Typography>
          {/* Asset rollforward reporting */}
          <Card title="Asset rollforward reporting">
            <ReportLink
              to="/reports/asset-rollforward/$tabType"
              params={{ tabType: 'inventory' }}
              icon={<AddShoppingCartOutlined />}
              title="Inventory rollforward"
              subtitle={
                <span>
                  Track <b>inventory asset flow</b> from starting to ending
                  positions over a time period
                </span>
              }
            />
            <ReportLink
              to="/reports/asset-rollforward/$tabType"
              params={{ tabType: 'fixed-assets' }}
              icon={<ShoppingCartCheckoutOutlined />}
              title="Fixed asset rollforward"
              subtitle={
                <span>
                  Track <b>fixed asset flow</b> from starting to ending
                  positions over a time period
                </span>
              }
            />
          </Card>
          <Card title="Depreciation schedule reporting">
            <ReportLink
              to="/reports/depreciation-schedule"
              icon={<TableChartOutlined />}
              title="Depreciation schedule"
              subtitle={
                <span>
                  Review <b>monthly depreciation</b> by asset across a selected
                  date range
                </span>
              }
            />
          </Card>
          <Card title="Asset bridge reporting">
            <ReportLink
              to="/reports/asset-bridge/$tabType"
              params={{ tabType: 'inventory' }}
              icon={<StackedBarChartOutlined />}
              title="Inventory bridge"
              subtitle={
                <span>
                  Track <b>inventory asset flow</b> across multiple time periods
                  as a time-series bridge
                </span>
              }
            />
            <ReportLink
              isDisabled
              params={{ tabType: 'fixed-assets' }}
              icon={<WaterfallChartOutlined />}
              title="Fixed asset bridge"
              subtitle={
                <span>
                  Track <b>fixed asset flow</b> across multiple time periods as
                  a time-series bridge
                </span>
              }
            />
          </Card>
        </Stack>

        {/* Right column */}
        <Stack flex={1} spacing={3}>
          <Typography variant="h6" paddingLeft={1}>
            Close management
          </Typography>
          {/* Exception handling - bottom left */}
          <Card title="Exception handling">
            <ReportLink
              to="/reports/exception/asset/$tabType"
              params={{ tabType: 'date' }}
              icon={<EventBusyOutlined />}
              title="Date exceptions"
              subtitle={
                <span>
                  Identify assets with <b>date inconsistencies</b> such as
                  in-service dates before inventory dates
                </span>
              }
            />
            <ReportLink
              to="/reports/exception/asset/$tabType"
              params={{ tabType: 'detail' }}
              icon={<PlaylistRemoveOutlined />}
              title="Detail exceptions"
              subtitle={
                <span>
                  Identify assets with <b>missing details</b> such as cost
                  basis, salvage value, or useful life
                </span>
              }
            />
          </Card>
          {/* Period close - bottom right */}
          <Card title="Period review and close">
            <ReportLink
              to="/accounting/initial-true-up"
              icon={<TuneOutlined />}
              title="Initial true-up"
              subtitle={
                <span>
                  Complete <b>one-time setup</b> to establish Hardfin as your
                  authoritative subledger
                </span>
              }
            />
            <ReportLink
              to="/accounting/close-workflow"
              icon={<LockOutlined />}
              title="Close workflow"
              subtitle={
                <span>
                  Complete the <b>month-end close</b> workflow to review journal
                  entries and lock the accounting period
                </span>
              }
            />
            <ReportLink
              to="/accounting/period-review"
              icon={<HistoryOutlined />}
              title="Prior periods"
              subtitle={
                <span>
                  Review <b>historical</b> accounting periods and their
                  associated journal entries
                </span>
              }
            />
          </Card>
        </Stack>
      </Stack>
    </PageContent>
  );
}
